import React from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Label
} from 'recharts';
import { Paper, Typography, Box, useTheme } from '@mui/material';
import { formatDateTime } from '../utils/dataUtils';

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <Paper
                elevation={3}
                sx={{
                    p: 1.5,
                    backgroundColor: 'rgba(255, 255, 255, 0.96)',
                    border: '1px solid #ccc',
                }}
            >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {formatDateTime(label)}
                </Typography>
                {payload.map((entry, index) => (
                    <Typography
                        key={index}
                        variant="body2"
                        sx={{ color: entry.color, mb: 0.5 }}
                    >
                        {`${entry.name}: ${Number(entry.value).toFixed(2)}`}
                    </Typography>
                ))}
            </Paper>
        );
    }
    return null;
};

const FEED_UNIT = 'Feed Flow (m³)';
const COND_UNIT = 'Cond Flow (m³)';
const POWER_FLOW_UNIT = 'Power Consumption (kW)'; // Change label for Y axis
const POWER_CONS_UNIT = 'Power Consumption (kW)';

const FEED_COLOR = '#82ca9d';
const COND_COLOR = '#ffc658';
const POWER_FLOW_COLOR = '#8884d8';
const POWER_CONS_COLOR = '#ff7300';

const DataGraph = ({ data, tableName }) => {
    const theme = useTheme();

    // Helper to get display name for table
    const getDisplayName = (name) => {
        if (!name) return '';
        if (name.toLowerCase() === 'dr-reddy') return 'DR REDDY 4T';
        if (name.toLowerCase() === 'dr-reddy-sez') return 'DR REDDY SEZ';
        if (name.toLowerCase() === 'laxmi-organics') return 'LAXMI ORGANICS';
        if (name.toLowerCase() === 'ks-textile') return 'KS TEXTILE';
        if (name.toLowerCase() === 'clear-choice') return 'CLEAR CHOICE';
        if (name.toLowerCase() === 'baddi') return 'BADDI';
        if (name.toLowerCase() === 'parle') return 'PARLE';
        // ...add more mappings as needed...
        return name.replace(/-/g, ' ').toUpperCase();
    };

    const getTodayData = (data) => {
        if (!Array.isArray(data)) return [];
        // Get today's date in UTC
        const now = new Date();
        const today = new Date(Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate(),
            0, 0, 0, 0
        ));
        const tomorrow = new Date(today);
        tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
        
        return data
            .filter(item => {
                const itemDate = new Date(item.DateTime || item.Datetime);
                return itemDate >= today && itemDate < tomorrow;
            })
            .sort((a, b) => {
                const dateA = new Date(a.DateTime || a.Datetime);
                const dateB = new Date(b.DateTime || b.Datetime);
                return dateA - dateB;
            });
    };

    // Find keys for all four fields if present
    const getDataKey = (type) => {
        // Prevent error if data is undefined or empty
        if (!Array.isArray(data) || data.length === 0) return '';
        if (tableName.toLowerCase() === 'mohali') {
            switch (type.toUpperCase()) {
                case 'POWER':
                    return 'EM_POWER_PRE_1HR';
                case 'POWER24':
                    return 'EM_POWER_PRE_24HR';
                default:
                    return '';
            }
        }
        if (tableName.toLowerCase() === 'laxmi-organics') {
            if (type.toUpperCase() === 'FEED') return 'FEED_FLOW_TOT_CUR_1H_LAXMI3';
            if (type.toUpperCase() === 'COND') return 'COND_FLOW_TOT_CUR_1H_LAXMI3';
            if (type.toUpperCase() === 'POWER') return 'EM_POWER_TOT_CUR_1H_LAXMI3';
            return '';
        }
        if (tableName.toLowerCase() === 'ks-textile') {
            if (type.toUpperCase() === 'FEED') return 'FEED_FLOW_PRE_1H_TOT_KS';
            if (type.toUpperCase() === 'COND') return 'COND_FLOW_PRE_1H_TOT_KS';
            if (type.toUpperCase() === 'POWER') return 'POWER_PRE_1H_TOT_KS';
            return '';
        }
        if (tableName.toLowerCase() === 'baddi') {
            if (type.toUpperCase() === 'POWER') return 'Baddi_Power_PRE_1h_TOT';
            if (type.toUpperCase() === 'POWER2') return 'EM2_POWER_PRE_1H_TOT_HMI';
            return '';
        }
        if (tableName.toLowerCase() === 'parle') {
            if (type.toUpperCase() === 'FEED') return 'FEED_FLOW_PRE_1H_TOT_PARLE';
            if (type.toUpperCase() === 'COND') return 'COND_FLOW_TOT_PRE_1HR_PARLE';
            if (type.toUpperCase() === 'POWER') return 'POWER_PRE_1H_TOT_PARLE';
            return '';
        }
        const keys = Object.keys(data[0]);
        if (type.toUpperCase() === 'FEED') return keys.find(key => key.toUpperCase().includes('FEED')) || '';
        if (type.toUpperCase() === 'COND') return keys.find(key => key.toUpperCase().includes('COND')) || '';
        if (type.toUpperCase() === 'POWER') {
            // Power Flow (not consumption)
            return keys.find(key =>
                key.toUpperCase().includes('POWER') &&
                !key.toUpperCase().includes('CONSUM') &&
                !key.toUpperCase().includes('EM_POWER_PRE_1HR') &&
                !key.toUpperCase().includes('EM_POWER_PRE_24HR')
            ) || '';
        }
        if (type.toUpperCase() === 'POWERCONSUMPTION') {
            // Power Consumption
            return keys.find(key =>
                key.toUpperCase().includes('CONSUM') ||
                key.toUpperCase().includes('EM_POWER_PRE_1HR') ||
                key.toUpperCase().includes('EM_POWER_PRE_24HR')
            ) || '';
        }
        return '';
    };

    const isMohali = tableName.toLowerCase() === 'mohali';
    const isBaddi = tableName.toLowerCase() === 'baddi';

    // Replace formatDateTime for XAxis tickFormatter to show only HH:mm
    const formatHourMinuteOnly = (dateTime) => {
        if (!dateTime) return '';
        // Handles "YYYY-MM-DD HH:mm:ss"
        if (dateTime.includes(' ')) {
            const time = dateTime.split(' ')[1] || '';
            const [hh, mm] = time.split(':');
            return `${hh}:${mm}`;
        }
        // Handles ISO format
        if (dateTime.includes('T')) {
            const t = dateTime.split('T')[1];
            if (t) {
                const [hh, mm] = t.split(':');
                return `${hh}:${mm}`;
            }
        }
        return dateTime;
    };

    // Get all yAxisIds used in the current data keys
    const getActiveYAxisIds = () => {
        const ids = [];
        if (isMohali) {
            ids.push('power1hr', 'power24hr');
        } else if (isBaddi) {
            ids.push('power1hr', 'power2hr');
        } else {
            if (getDataKey('FEED')) ids.push('feed');
            if (getDataKey('COND')) ids.push('cond');
            if (getDataKey('POWER')) ids.push('powerflow');
            if (getDataKey('POWERCONSUMPTION')) ids.push('powercons');
        }
        return ids;
    };

    return (
        <Paper 
            elevation={3} 
            sx={{ 
                p: 3,
                mb: 3,
                background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                    boxShadow: theme.shadows[6],
                    transform: 'translateY(-2px)'
                }
            }}
        >
            <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                    mb: 3,
                    fontWeight: 600,
                    color: theme.palette.primary.main 
                }}
            >
                {getDisplayName(tableName)} - {(isMohali || isBaddi) ? 'Power Consumption Monitoring' : 'Real-Time Flow Monitoring'}
            </Typography>
            
            <Box sx={{ width: '100%', height: { xs: 400, sm: 500, md: 600 } }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={getTodayData(data)}
                        margin={{ top: 20, right: 80, left: 20, bottom: 65 }}
                    >
                        <CartesianGrid 
                            strokeDasharray="3 3" 
                            stroke="rgba(0,0,0,0.1)"
                        />
                        <XAxis
                            dataKey="DateTime"
                            tickFormatter={formatHourMinuteOnly}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                        >
                            <Label 
                                value="Time" 
                                position="bottom" 
                                offset={50}
                                style={{ textAnchor: 'middle', fill: theme.palette.text.primary }}
                            />
                        </XAxis>
                        {/* Dynamically render YAxis only for active yAxisIds */}
                        {getActiveYAxisIds().includes('feed') && (
                            <YAxis
                                yAxisId="feed"
                                label={{
                                    value: FEED_UNIT,
                                    angle: -90,
                                    position: "insideLeft",
                                    style: { textAnchor: 'middle', fill: FEED_COLOR, fontWeight: 600 }
                                }}
                                stroke={FEED_COLOR}
                            />
                        )}
                        {getActiveYAxisIds().includes('cond') && (
                            <YAxis
                                yAxisId="cond"
                                orientation="right"
                                label={{
                                    value: COND_UNIT,
                                    angle: 90,
                                    position: "insideRight",
                                    style: { textAnchor: 'middle', fill: COND_COLOR, fontWeight: 600 }
                                }}
                                stroke={COND_COLOR}
                                width={60}
                                tick={{ dx: 10 }}
                            />
                        )}
                        {getActiveYAxisIds().includes('powerflow') && (
                            <YAxis
                                yAxisId="powerflow"
                                orientation="right"
                                label={{
                                    value: POWER_FLOW_UNIT,
                                    angle: 90,
                                    position: "outsideRight",
                                    style: {
                                        textAnchor: 'middle',
                                        fill: POWER_FLOW_COLOR,
                                        fontWeight: 700,
                                        fontSize: 16,
                                        letterSpacing: 1,
                                        fontFamily: 'Roboto, Arial, sans-serif'
                                    }
                                }}
                                stroke={POWER_FLOW_COLOR}
                                width={90}
                                tick={{ dx: 60, fill: POWER_FLOW_COLOR, fontWeight: 600, fontSize: 14 }}
                                axisLine={{ stroke: POWER_FLOW_COLOR, strokeWidth: 2 }}
                            />
                        )}
                        {getActiveYAxisIds().includes('powercons') && (
                            <YAxis
                                yAxisId="powercons"
                                orientation="right"
                                label={{
                                    value: POWER_CONS_UNIT,
                                    angle: 90,
                                    position: "outsideRight",
                                    offset: 60,
                                    style: { textAnchor: 'middle', fill: POWER_CONS_COLOR, fontWeight: 600 }
                                }}
                                stroke={POWER_CONS_COLOR}
                                width={60}
                                tick={{ dx: 90 }}
                            />
                        )}
                        {/* Mohali/Baddi special axes */}
                        {isMohali && (
                            <>
                                <YAxis
                                    yAxisId="power1hr"
                                    label={{
                                        value: 'Power (1 Hour) (kW)',
                                        angle: -90,
                                        position: "insideLeft",
                                        style: { textAnchor: 'middle', fontWeight: 600, fill: '#8884d8' }
                                    }}
                                    stroke="#8884d8"
                                    width={70}
                                />
                                <YAxis
                                    yAxisId="power24hr"
                                    orientation="right"
                                    label={{
                                        value: 'Power (24 Hours) (kW)',
                                        angle: 90,
                                        position: "insideRight",
                                        style: { textAnchor: 'middle', fontWeight: 600, fill: '#82ca9d' }
                                    }}
                                    stroke="#82ca9d"
                                    width={70}
                                    tick={{ dx: 10 }}
                                />
                            </>
                        )}
                        {isBaddi && (
                            <>
                                <YAxis
                                    yAxisId="power1hr"
                                    label={{
                                        value: 'Baddi Power (1 Hour) (kW)',
                                        angle: -90,
                                        position: "insideLeft",
                                        style: { textAnchor: 'middle', fontWeight: 600, fill: '#8884d8' }
                                    }}
                                    stroke="#8884d8"
                                    width={70}
                                />
                                <YAxis
                                    yAxisId="power2hr"
                                    orientation="right"
                                    label={{
                                        value: 'EM2 Power (1 Hour) (kW)',
                                        angle: 90,
                                        position: "insideRight",
                                        style: { textAnchor: 'middle', fontWeight: 600, fill: '#82ca9d' }
                                    }}
                                    stroke="#82ca9d"
                                    width={70}
                                    tick={{ dx: 10 }}
                                />
                            </>
                        )}
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                            verticalAlign="top" 
                            height={36}
                            wrapperStyle={{ paddingBottom: '20px' }}
                        />
                        {isMohali ? (
                            <>
                                <Line
                                    type="monotone"
                                    dataKey="EM_POWER_PRE_1HR"
                                    name="Power (1 Hour) (kW)"
                                    stroke="#8884d8"
                                    dot={false}
                                    strokeWidth={2}
                                    yAxisId="power1hr"
                                    activeDot={{ r: 6 }}
                                    animationDuration={1000}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="EM_POWER_PRE_24HR"
                                    name="Power (24 Hours) (kW)"
                                    stroke="#82ca9d"
                                    dot={false}
                                    strokeWidth={2}
                                    yAxisId="power24hr"
                                    activeDot={{ r: 6 }}
                                    animationDuration={1000}
                                />
                            </>
                        ) : isBaddi ? (
                            <>
                                <Line
                                    type="monotone"
                                    dataKey="Baddi_Power_PRE_1h_TOT"
                                    name="Baddi Power (1 Hour) (kW)"
                                    stroke="#8884d8"
                                    dot={false}
                                    strokeWidth={2}
                                    yAxisId="power1hr"
                                    activeDot={{ r: 6 }}
                                    animationDuration={1000}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="EM2_POWER_PRE_1H_TOT_HMI"
                                    name="EM2 Power (1 Hour) (kW)"
                                    stroke="#82ca9d"
                                    dot={false}
                                    strokeWidth={2}
                                    yAxisId="power2hr"
                                    activeDot={{ r: 6 }}
                                    animationDuration={1000}
                                />
                            </>
                        ) : (
                            <>
                                {getDataKey('FEED') && (
                                    <Line
                                        type="monotone"
                                        dataKey={getDataKey('FEED')}
                                        name={FEED_UNIT}
                                        stroke={FEED_COLOR}
                                        dot={false}
                                        strokeWidth={2}
                                        yAxisId="feed"
                                        activeDot={{ r: 6 }}
                                        animationDuration={1000}
                                    />
                                )}
                                {getDataKey('COND') && (
                                    <Line
                                        type="monotone"
                                        dataKey={getDataKey('COND')}
                                        name={COND_UNIT}
                                        stroke={COND_COLOR}
                                        dot={false}
                                        strokeWidth={2}
                                        yAxisId="cond"
                                        activeDot={{ r: 6 }}
                                        animationDuration={1000}
                                    />
                                )}
                                {getDataKey('POWER') && (
                                    <Line
                                        type="monotone"
                                        dataKey={getDataKey('POWER')}
                                        name={POWER_FLOW_UNIT}
                                        stroke={POWER_FLOW_COLOR}
                                        dot={false}
                                        strokeWidth={2}
                                        yAxisId="powerflow"
                                        activeDot={{ r: 6 }}
                                        animationDuration={1000}
                                    />
                                )}
                            </>
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        </Paper>
    );
};

export default DataGraph;
